import { api } from '@/shared/axios-client';
import { queryOptions, useQuery } from '@tanstack/react-query';

// ── Request ─────────────────────────────────────────────────────────────────

export interface ScanDetailsRequest {
  repoId: string;
  branchId: string;
}

// ── Response types ──────────────────────────────────────────────────────────

export interface ScanLines {
  total: number;
  executable: number;
  boilerplate: number;
}

export interface ScanInventory {
  files: number;
  entities: number;
  classes: number;
  methods: number;
  branches: number;
  functions: number;
  loops: number;
  lines: ScanLines;
}

export interface CoverageMetric {
  total: number;
  covered: number;
  percentage: number;
}

export interface UncoveredMetric {
  percentage: number;
  uncovered: number;
  total: number;
}

export interface TestAutomationMetric {
  percentage: number;
  covered: number;
  total: number;
  testCount: number;
}

export interface CoverageModeSummary {
  totalCoverage: CoverageMetric;
  testAutomation: TestAutomationMetric;
  manualTests: TestAutomationMetric;
  uncovered: UncoveredMetric;
}

export interface CoverageByCodeElement {
  classes: CoverageMetric;
  methods: CoverageMetric;
  branches: CoverageMetric;
  functions: CoverageMetric;
  loops: CoverageMetric;
}

export interface InventoryModeCoverage {
  summary: CoverageModeSummary;
  coverageByCodeElement: CoverageByCodeElement;
}

export interface LinesOfCodeModeCoverage {
  summary: CoverageModeSummary;
}

export interface ScanCoverage {
  line: CoverageMetric;
  function: CoverageMetric;
  class: CoverageMetric;
  method: CoverageMetric;
  inventoryMode: InventoryModeCoverage;
  linesOfCodeMode: LinesOfCodeModeCoverage;
}

export interface DerivedCoverage {
  inventoryMode: InventoryModeCoverage;
  linesOfCodeMode: LinesOfCodeModeCoverage;
}

export interface ScanDetail {
  _id: string;
  scanRef: string;
  scanNumber: number;
  repoId: string;
  branchId: string;
  triggerType: string;
  status: string;
  startedAt: string;
  commitId: string;
  inventory: ScanInventory;
  coverage: ScanCoverage;
  buildId: number;
  buildNumber: string;
  buildUrl: string;
  pipelineId: number;
  pipelineName: string;
  triggeredBy: string;
  buildStartTime: string;
  buildFinishTime: string;
  completedAt: string;
  derivedCoverage: DerivedCoverage;
  branchName: string;
}

export interface ScanDetailsResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: ScanDetail[];
}

// ── Fetcher ─────────────────────────────────────────────────────────────────

export const getScanDetails = (
  params: ScanDetailsRequest,
): Promise<ScanDetailsResponse> => {
  return api.get('/api/scansDetails', { params });
};

// ── Query options ───────────────────────────────────────────────────────────

export const getScanDetailsQueryOptions = (params: ScanDetailsRequest) => {
  return queryOptions({
    queryKey: ['scan-details', params.repoId, params.branchId],
    queryFn: () => getScanDetails(params),
    enabled: !!params.repoId && !!params.branchId,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

// ── Hook ────────────────────────────────────────────────────────────────────

export const useScanDetails = (params: ScanDetailsRequest) => {
  return useQuery({
    ...getScanDetailsQueryOptions(params),
  });
};
